// Reducer state interface
export interface FilterState {
  activeTab: 'popular' | 'newest' | 'best';
  searchQuery: string;
  sortBy: 'popular' | 'newest' | 'best' | 'updated' | 'hotNew' | 'bestMonth';
  currentPage: number;
  selectedFandoms: string[];
  selectedTags: string[];
  selectedUniverses: string[];
  selectedStatuses: string[];
  selectedRatings: string[];
  selectedDirections: string[];
  selectedCharacters: string[];
  selectedPairingCharacters: string[];
  isMobileFiltersOpen: boolean;
}

// Action types for reducer
export type FilterAction =
  | { type: 'SET_ACTIVE_TAB'; payload: 'popular' | 'newest' | 'best' }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | {
      type: 'SET_SORT_BY';
      payload: 'popular' | 'newest' | 'best' | 'updated' | 'hotNew' | 'bestMonth';
    }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_FANDOMS'; payload: string[] }
  | { type: 'SET_TAGS'; payload: string[] }
  | { type: 'SET_UNIVERSES'; payload: string[] }
  | { type: 'SET_STATUSES'; payload: string[] }
  | { type: 'SET_RATINGS'; payload: string[] }
  | { type: 'SET_DIRECTIONS'; payload: string[] }
  | { type: 'SET_CHARACTERS'; payload: string[] }
  | { type: 'SET_PAIRING_CHARACTERS'; payload: string[] }
  | { type: 'SET_MOBILE_FILTERS_OPEN'; payload: boolean }
  | { type: 'SYNC_FROM_URL'; payload: Partial<FilterState> }
  | { type: 'RESET_PAGE' };

function getNextActiveTab(
  sortBy: FilterState['sortBy'],
  currentActiveTab: FilterState['activeTab'],
): FilterState['activeTab'] {
  if (sortBy === 'updated' || sortBy === 'hotNew' || sortBy === 'bestMonth') {
    return currentActiveTab;
  }

  return sortBy;
}

// Reducer function for managing filter state
export const filterReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload, sortBy: action.payload, currentPage: 1 };
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'SET_SORT_BY':
      return {
        ...state,
        sortBy: action.payload,
        activeTab: getNextActiveTab(action.payload, state.activeTab),
        currentPage: 1,
      };
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_FANDOMS':
      return {
        ...state,
        selectedFandoms: action.payload,
        selectedPairingCharacters: [],
        currentPage: 1,
      };
    case 'SET_TAGS':
      return { ...state, selectedTags: action.payload, currentPage: 1 };
    case 'SET_UNIVERSES':
      return { ...state, selectedUniverses: action.payload, currentPage: 1 };
    case 'SET_STATUSES':
      return { ...state, selectedStatuses: action.payload, currentPage: 1 };
    case 'SET_RATINGS':
      return { ...state, selectedRatings: action.payload, currentPage: 1 };
    case 'SET_DIRECTIONS':
      return { ...state, selectedDirections: action.payload, currentPage: 1 };
    case 'SET_CHARACTERS':
      return {
        ...state,
        selectedCharacters: action.payload,
        selectedPairingCharacters: [],
        currentPage: 1,
      };
    case 'SET_PAIRING_CHARACTERS':
      return { ...state, selectedPairingCharacters: action.payload, currentPage: 1 };
    case 'SET_MOBILE_FILTERS_OPEN':
      return { ...state, isMobileFiltersOpen: action.payload };
    case 'SYNC_FROM_URL':
      return {
        ...state,
        ...action.payload,
        activeTab: getNextActiveTab(
          action.payload.sortBy ?? state.sortBy,
          action.payload.activeTab ?? state.activeTab,
        ),
      };
    case 'RESET_PAGE':
      return state.currentPage === 1 ? state : { ...state, currentPage: 1 };
    default:
      return state;
  }
};
